import { useState } from "react";
import { View, Text } from "../base";
import { Icon } from "../base/Icon";
import { SpanDetailTabs, TabDefinition } from "./SpanDetailTabs";
import { RawSpanDetailView, RawSpanData } from "./RawSpanDetailView";
import { safeString } from "../../utils/strings";

export interface LoggerSpanData {
  level: string;
  message: string;
  location?: string;
  attributes?: Record<string, unknown>;
}

const getLevelColor = (level: string): string => {
  switch (level?.toLowerCase()) {
    case "error":
      return "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800";
    case "warn":
      return "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800";
    case "debug":
      return "text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700";
    case "info":
    default:
      return "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800";
  }
};

const getLevelIcon = (level: string): string => {
  switch (level?.toLowerCase()) {
    case "error":
    case "warn":
      return "error";
    case "info":
    case "debug":
    default:
      return "info";
  }
};

const LoggerContent = ({ data }: { data: LoggerSpanData }) => {
  return (
    <View className="space-y-4">
      {/* Level Badge */}
      <View className="flex flex-row items-center gap-2">
        <View className={`flex flex-row items-center gap-1.5 px-2.5 py-1 rounded-md border ${getLevelColor(data.level)}`}>
          <Icon
            name={getLevelIcon(data.level)}
            size={14}
          />
          <Text className="text-xs font-medium uppercase">{data.level}</Text>
        </View>
      </View>

      {/* Message */}
      <View>
        <View className="flex flex-row items-center gap-2 mb-2">
          <Icon
            name="message"
            size={14}
            className="text-gray-600 dark:text-gray-300"
          />
          <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Message</Text>
        </View>
        <View className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
          <Text className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(data.message)}</Text>
        </View>
      </View>

      {/* Metadata */}
      <View className="flex flex-row flex-wrap gap-4">
        {data.location && (
          <View className="flex flex-row items-center gap-1.5">
            <Icon
              name="code"
              size={14}
              className="text-gray-400"
            />
            <Text className="text-xs text-gray-600 dark:text-gray-400">
              <span className="font-mono">{safeString(data.location)}</span>
            </Text>
          </View>
        )}
      </View>

      {/* Extra Attributes */}
      {data.attributes && Object.keys(data.attributes).length > 0 && (
        <View>
          <View className="flex flex-row items-center gap-2 mb-2">
            <Icon
              name="list"
              size={14}
              className="text-gray-600 dark:text-gray-300"
            />
            <Text className="text-sm font-medium text-gray-700 dark:text-gray-300">Attributes</Text>
          </View>
          <View className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 space-y-1">
            {Object.entries(data.attributes).map(([key, value]) => (
              <View
                key={key}
                className="flex flex-row items-start gap-2"
              >
                <Text className="text-xs font-mono text-gray-500 dark:text-gray-400 flex-shrink-0">{key}:</Text>
                <Text className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{safeString(String(value))}</Text>
              </View>
            ))}
          </View>
        </View>
      )}
    </View>
  );
};

interface LoggerSpanDetailViewProps {
  data: LoggerSpanData;
  rawData: RawSpanData;
  className?: string;
}

const tabs: TabDefinition[] = [
  { id: "logger", label: "Log", color: "gray" },
  { id: "attributes", label: "Attributes", color: "gray" },
];

export const LoggerSpanDetailView = ({ data, rawData, className }: LoggerSpanDetailViewProps) => {
  const [activeTab, setActiveTab] = useState("logger");

  return (
    <View className={`flex flex-col overflow-hidden h-full ${className || ""}`}>
      <SpanDetailTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      <View className="flex-1 overflow-y-auto p-4">{activeTab === "logger" ? <LoggerContent data={data} /> : <RawSpanDetailView data={rawData} />}</View>
    </View>
  );
};

export const parseLoggerSpanData = (attributes: Record<string, unknown>): LoggerSpanData | null => {
  const level = attributes["log.level"];
  const message = attributes["log.message"];
  if (typeof level !== "string" || typeof message !== "string") return null;

  const location = typeof attributes["log.location"] === "string" ? attributes["log.location"] : undefined;

  // Collect any extra log.* attributes
  const extraAttributes: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (key.startsWith("log.") && !["log.level", "log.message", "log.location"].includes(key)) {
      extraAttributes[key.replace("log.", "")] = value;
    }
  }

  return {
    level,
    message,
    location,
    attributes: Object.keys(extraAttributes).length > 0 ? extraAttributes : undefined,
  };
};
